import type { BrowseGame } from "@/blocks/GamificationCard/admin/types";
import type { ChallengeZoneGame } from "./types";

/**
 * Sort games for maximum engagement and enrich with display metadata.
 *
 * 1. Sort by participationCount descending
 * 2. Within tiers (counts within 10% of tier leader), sort by recency
 * 3. Diversify: if two adjacent cards share the same type, swap the second
 *    with the next card of a different type
 * 4. Mark top 20% as HOT
 */
export function sortAndEnrichGames(games: BrowseGame[]): ChallengeZoneGame[] {
    if (games.length === 0) return [];

    // Step 1 + 2: Sort by participation, with recency tiebreaker within tiers
    const sorted = [...games].sort((a, b) => {
        const countDiff = b.participationCount - a.participationCount;
        // If within 10% of each other, prefer newer
        const maxCount = Math.max(a.participationCount, b.participationCount);
        if (maxCount > 0 && Math.abs(countDiff) / maxCount <= 0.1) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
        return countDiff;
    });

    // Step 3: Diversify adjacent types
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].type === sorted[i - 1].type) {
            // Find next card with a different type
            const swapIdx = sorted.findIndex((g, j) => j > i && g.type !== sorted[i - 1].type);
            if (swapIdx !== -1) {
                [sorted[i], sorted[swapIdx]] = [sorted[swapIdx], sorted[i]];
            }
        }
    }

    // Step 4: Determine HOT threshold (top 20%)
    const hotCount = Math.max(1, Math.ceil(sorted.length * 0.2));
    const hotThreshold = sorted.length > 1 ? sorted[hotCount - 1].participationCount : sorted[0].participationCount;

    // Enrich with display metadata
    return sorted.map((game) => ({
        ...game,
        isHot: game.participationCount >= hotThreshold && game.participationCount > 0,
        ctaKey: game.type === "poll" ? ("voteNow" as const) : ("playNow" as const),
        participationKey: game.type === "poll" ? ("votes" as const) : ("played" as const),
    }));
}
